/**
 * services/TradingSetupSettingsService.ts
 * Trading setup settings management service
 *
 * Provides singleton settings for Trading Setup default preferences.
 * These defaults pre-fill new SessionDrafts to reduce daily friction —
 * the trader still confirms (or modifies) values each session.
 */

import { BaseService } from './BaseService';
import { db } from '@/lib/db';
import { syncCoordinatorService } from './sync/SyncCoordinatorService';
import { markAsInstrumentedWriteContext } from './sync/SyncImportContext';
import { buildDiffOps } from './sync/OperationBuilder';
import { getSyncOperationService } from './sync/SyncOperationService';
import type { TradingSetupSettings } from '@/types/schema/TradingSetupSettings';
import type {
  TradingSetupDefaults,
  TradingSetupDefaultsUpdate,
} from '@/types/shared/TradingSetupTypes';

/**
 * Service for managing trading setup settings
 *
 * Extends BaseService to provide standard CRUD operations plus
 * specialized methods for getting or creating default settings.
 */
export class TradingSetupSettingsService extends BaseService<TradingSetupSettings> {
  constructor() {
    super('trading_setup_settings');
  }

  /**
   * Get default trading setup settings
   *
   * @returns Default settings object (without id, timestamps, or sync fields)
   * @private
   */
  private getDefaultSettings(): Omit<
    TradingSetupSettings,
    'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'deletedByDevice'
  > {
    return {
      defaultStrategyPlaybookPairs: [],
      defaultFocusInstrumentIds: [],
    };
  }

  /**
   * Get existing settings or create default settings if none exist
   *
   * This is the primary method for retrieving trading setup settings.
   * On first use, it creates an empty settings record that the user
   * can then customize with their preferred defaults.
   *
   * @returns Promise resolving to TradingSetupSettings
   */
  async getOrCreateDefaultSettings(): Promise<TradingSetupSettings> {
    try {
      const allSettings = await this.getAll();

      if (allSettings.length > 0) {
        const firstSetting = allSettings[0];
        if (!firstSetting) {
          throw new Error('Unexpected undefined settings record');
        }
        return firstSetting;
      }

      const defaultSettings = this.getDefaultSettings();
      const createdSettings = await this.add(defaultSettings);

      return createdSettings;
    } catch (error) {
      console.error('Failed to get or create trading setup settings:', error);
      throw new Error('Failed to initialize trading setup settings');
    }
  }

  /**
   * Get current trading setup settings
   *
   * Alias for getOrCreateDefaultSettings() for semantic clarity.
   * Required by useLiveSettings consumers.
   *
   * @returns Promise resolving to current TradingSetupSettings
   */
  async getCurrentSettings(): Promise<TradingSetupSettings> {
    return this.getOrCreateDefaultSettings();
  }

  /**
   * Save trading setup defaults via full-object replacement.
   *
   * Uses table.put() instead of table.update() so that absent optional
   * fields (specifically defaultAccountId) are actually removed from the
   * stored record. Dexie's table.update() is a partial merge and cannot
   * delete keys — see SessionDraftService.updateTradingSetup() for the
   * same pattern.
   *
   * @param id - Settings record ID
   * @param defaults - The three default fields to persist
   */
  async saveDefaults(
    id: string,
    defaults: TradingSetupDefaultsUpdate
  ): Promise<void> {
    let didWrite = false;

    await db.transaction(
      'rw',
      [this.table, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();

        const existing = await this.table.get(id);
        if (!existing) {
          throw new Error(`trading_setup_settings with id ${id} not found`);
        }

        // Clone and apply updates
        const candidate: TradingSetupSettings = { ...existing };
        candidate.defaultStrategyPlaybookPairs =
          defaults.defaultStrategyPlaybookPairs;
        candidate.defaultFocusInstrumentIds =
          defaults.defaultFocusInstrumentIds;

        if ('defaultAccountId' in defaults) {
          candidate.defaultAccountId = defaults.defaultAccountId;
        } else {
          delete candidate.defaultAccountId;
        }

        // Build shadow object for diff — signal cleared fields with null
        const afterForDiff: Record<string, unknown> = {
          ...(candidate as unknown as Record<string, unknown>),
        };
        if (
          !('defaultAccountId' in defaults) &&
          existing.defaultAccountId !== undefined
        ) {
          afterForDiff['defaultAccountId'] = null;
        }

        // Compute diff ops (before touching updatedAt to avoid false positives)
        const ops = buildDiffOps(
          'trading_setup_settings',
          id,
          existing as unknown as Record<string, unknown>,
          afterForDiff
        );

        // If no data changes, skip the write
        if (ops.length === 0) {
          return;
        }

        // Set updatedAt since we bypass BaseService.update()
        const now = new Date().toISOString();
        candidate.updatedAt = now;
        ops.push({
          table: 'trading_setup_settings',
          recordId: id,
          op: 'set',
          field: 'updatedAt',
          value: now,
        });

        await this.table.put(candidate);
        await getSyncOperationService().logBatch(ops);
        didWrite = true;
      }
    );

    // Notify after transaction commits (not on no-op early return)
    if (didWrite) {
      syncCoordinatorService.notifyChange('trading_setup_settings', id);
    }
  }
}

// Export singleton instance for use throughout the application
export const tradingSetupSettingsService = new TradingSetupSettingsService();

/**
 * Extract TradingSetupDefaults from TradingSetupSettings
 *
 * Bridges settings → draft pre-fill by picking the default fields
 * that createEmptySessionDraft() consumes. Field names are identical
 * between the two types by design, so this is a simple pick.
 *
 * @param settings - The current TradingSetupSettings record
 * @returns TradingSetupDefaults for draft pre-fill
 */
export function extractTradingSetupDefaults(
  settings: TradingSetupSettings
): TradingSetupDefaults {
  return {
    defaultStrategyPlaybookPairs: settings.defaultStrategyPlaybookPairs,
    defaultFocusInstrumentIds: settings.defaultFocusInstrumentIds,
    ...('defaultAccountId' in settings
      ? { defaultAccountId: settings.defaultAccountId }
      : {}),
  };
}
